import { useCallback, useMemo, useState } from "react";
import {
  useBoard,
  useBoardTasks,
  collectTaskTags,
  getTagStyle,
} from "@onetasklist/shared";
import type { BoardConfiguration, TaskSummary } from "@onetasklist/shared";
import { useApi } from "@api/useApi";
import { useBoardFilters } from "@contexts/BoardFiltersContext";
import { useBoardId } from "@contexts/BoardContext";
import type {
  SidebarTagFilterItem,
  SidebarTagFiltersViewModel,
} from "./SidebarTagFilters.ViewModel.types";

/** How many tags the section shows before the rest go behind "show more". */
export const VISIBLE_TAG_LIMIT = 5;

/**
 * A tag has no logo, so collapsed it needs something to be recognised by. Its
 * colour alone cannot carry that — plenty of tags have none — so the swatch
 * shows these, which are the tag's label at that width.
 *
 * Two letters from a single word, otherwise the first letter of the first two.
 *
 * @param tag The tag name.
 * @returns One or two upper-case letters.
 */
export const getTagInitials = (tag: string): string => {
  const words = tag.trim().split(/[\s_\-/]+/).filter(Boolean);

  if (words.length === 0) {
    return "?";
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Counts open tasks per tag, and seeds every tag the board uses so one that
 * appears only on done tasks still lists, at zero.
 */
const countOpenTasksByTag = (tasks: TaskSummary[]): Map<string, number> => {
  const counts = new Map<string, number>();

  for (const tag of collectTaskTags(tasks)) {
    counts.set(tag, 0);
  }

  for (const task of tasks) {
    if (task.isDone) {
      continue;
    }

    for (const tag of collectTaskTags([task])) {
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
  }

  return counts;
};

export const useSidebarTagFiltersViewModel = (): SidebarTagFiltersViewModel => {
  const api = useApi();
  const boardId = useBoardId();
  const { selectedTagFilters, toggleTagFilter } = useBoardFilters();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSectionOpen, setIsSectionOpen] = useState(true);

  const { data: board } = useBoard(api.boards, boardId ?? "");
  const { data: boardTasksData } = useBoardTasks(api.tasks, boardId ?? "", {
    enabled: !!boardId,
  });

  const tagColours = useMemo(
    () =>
      ((board?.configuration as BoardConfiguration | undefined)?.tagColours ??
        {}) as Record<string, string>,
    [board],
  );

  // Busiest first, like the connections above them, so the few on show are the
  // ones worth filtering by. Ties fall back to the name to keep it stable.
  const tags = useMemo<SidebarTagFilterItem[]>(() => {
    const counts = countOpenTasksByTag(boardTasksData?.tasks ?? []);

    return [...counts.entries()]
      .map(([tag, count]) => ({
        tag,
        count,
        style: getTagStyle(tagColours, tag),
        initials: getTagInitials(tag),
        isSelected: selectedTagFilters.includes(tag),
      }))
      .sort(
        (left, right) =>
          right.count - left.count ||
          left.tag.localeCompare(right.tag, undefined, {
            sensitivity: "base",
          }),
      );
  }, [boardTasksData, tagColours, selectedTagFilters]);

  // A selected tag always shows, wherever it sorts — otherwise turning a filter
  // off would mean expanding the section to find it again.
  const visibleTags = useMemo(() => {
    if (isExpanded) {
      return tags;
    }

    return tags.filter(
      (item, index) => index < VISIBLE_TAG_LIMIT || item.isSelected,
    );
  }, [tags, isExpanded]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((value) => !value);
  }, []);

  const toggleSection = useCallback(() => {
    setIsSectionOpen((value) => !value);
  }, []);

  return {
    visibleTags,
    hiddenCount: tags.length - visibleTags.length,
    isExpanded,
    hasTags: tags.length > 0,
    isSectionOpen,
    toggleExpanded,
    toggleSection,
    toggleTag: toggleTagFilter,
  };
};
